import { datadogRum } from '@datadog/browser-rum';
import { LDRecord } from '@launchdarkly/session-replay';
import { useEffect } from 'react';

import PublicEnv from 'shared/utils/public-env';

// Polls until both the DD RUM session ID and the LD session are available,
// then attaches the RUM session info to the LD recording as custom properties.
// This gives the LD Sessions UI a click-out into the matching DD RUM session
// (frustration signals, errors, long tasks, network waterfall, etc.).

// Pattern mirrors GetDutchie/brands-portal#296. Two intentional deviations:
//   1. We source the URL from `getInternalContext().session_id` rather than
//      `getSessionReplayLink()`, because admin keeps `sessionReplaySampleRate: 0`
//      (LD already records video). RUM Explorer works for 100% of sessions;
//      `getSessionReplayLink()` would return undefined for un-sampled sessions.
//   2. Property names are RUM-specific (`datadogRumSession*`) since this links
//      to DD's RUM Explorer, not DD Session Replay.

const POLL_INTERVAL_MS = 1000;
const MAX_ATTEMPTS = 30;
const DD_SITE = 'app.datadoghq.com';

function buildRumSessionUrl(sessionId: string): string {
  // RUM Explorer scoped to this session ID. Stable URL surface; opens DD's
  // session view with all RUM events even when DD session replay isn't sampled.
  const query = encodeURIComponent(`@session.id:${sessionId}`);
  return `https://${DD_SITE}/rum/explorer?query=${query}`;
}

export function useLinkDatadogRumToLd(): void {
  useEffect(() => {
    // Production-only: DD RUM is also production-only (apps/admin/src/utils/rum.ts),
    // so getInternalContext() returns undefined in lower envs. Bail early to avoid
    // wasted polling that would never produce a usable session id.
    if (PublicEnv.appEnv !== 'production') {
      return undefined;
    }

    let attempts = 0;

    const interval = window.setInterval(() => {
      attempts += 1;

      let ldSessionActive = false;
      try {
        // Truthy check covers null (between sessions) and undefined (before plugin
        // registers). `addSessionProperties` would throw on a non-active session.
        ldSessionActive = Boolean(LDRecord.getSession());
      } catch {
        ldSessionActive = false;
      }

      let rumSessionId: string | undefined;
      try {
        rumSessionId = datadogRum.getInternalContext()?.session_id;
      } catch {
        rumSessionId = undefined;
      }

      if (ldSessionActive && rumSessionId) {
        try {
          LDRecord.addSessionProperties({
            datadogRumSessionId: rumSessionId,
            datadogRumSessionUrl: buildRumSessionUrl(rumSessionId),
          });
        } catch (error) {
          console.warn('[LD] failed to push Datadog RUM session info to LD', error);
        }
        window.clearInterval(interval);
        return;
      }

      if (attempts >= MAX_ATTEMPTS) {
        window.clearInterval(interval);
        console.warn(
          `[LD] gave up attaching DD RUM session info after ${MAX_ATTEMPTS} polls; ` +
            'LD recording and/or DD RUM never both initialized'
        );
      }
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, []);
}
